import {
    ComplianceAutomationActionType,
    ComplianceAutomationType,
} from 'src/api_openapi/v2/compliance/automationSchemas';
import {
    POPUP_APPLIED_ACTION_OPTIONS,
    POPUP_COMPLIANCE_RESTRICTION_PERIOD_OPTIONS,
    POPUP_DEPOSIT_RESTRICTION_PERIOD_OPTIONS,
    PopupAppliedActionFormValueType,
    PopupAppliedActionRestrictionPeriodFormValueType,
    PopupAppliedActionType,
    showsAppliedActionRestrictionPeriod,
} from './MarkersOfHarmPopupTab.types';

export const MARKERS_OF_HARM_AUTOMATION_SOURCE = 'automation';

const SHOW_POPUP_EXECUTION_ORDER = 1;
const SET_ACCOUNT_STATUS_EXECUTION_ORDER = 2;

export type MarkersOfHarmAppliedActionSettingsType = {
    appliedAction: PopupAppliedActionType;
    restrictionPeriod?: PopupAppliedActionRestrictionPeriodFormValueType;
};

const getAppliedActionDescription = (appliedAction: PopupAppliedActionType): string =>
    POPUP_APPLIED_ACTION_OPTIONS.find((option) => option.value === appliedAction)?.description ??
    'Markers of Harm Pop-Up automation action.';

const parseComplianceRestrictionDays = (
    restrictionPeriod: PopupAppliedActionRestrictionPeriodFormValueType
): number => {
    switch (restrictionPeriod) {
        case '7-days':
            return 7;
        case '14-days':
            return 14;
        case '30-days':
            return 30;
        default:
            return 7;
    }
};

const buildDepositRestrictionConfig = (
    restrictionPeriod: PopupAppliedActionRestrictionPeriodFormValueType
): Record<string, unknown> => {
    if (restrictionPeriod === 'permanent') {
        return {
            // spelling on backend is wrong, don't change it
            permament: true,
        };
    }

    const suspensionDurationByPeriod: Partial<Record<PopupAppliedActionRestrictionPeriodFormValueType, number>> = {
        '1-hour': 1,
        '24-hours': 24,
        '48-hours': 48,
        '72-hours': 72,
        '1-week': 168,
    };

    return {
        // spelling on backend is wrong, don't change it
        permament: false,
        'suspension-duration': suspensionDurationByPeriod[restrictionPeriod] ?? 24,
    };
};

const buildSetAccountStatusConfig = (
    appliedAction: Exclude<PopupAppliedActionType, 'none'>,
    restrictionPeriod: PopupAppliedActionRestrictionPeriodFormValueType
): Record<string, unknown> | null => {
    const description = getAppliedActionDescription(appliedAction);

    if (appliedAction === 'compliance-restriction') {
        if (restrictionPeriod === '') {
            return null;
        }

        return {
            'status-to-set': 'compliance-restriction',
            description,
            source: 'automation',
            'suspension-duration': parseComplianceRestrictionDays(restrictionPeriod),
        };
    }

    if (restrictionPeriod === '') {
        return null;
    }

    return {
        'status-to-set': 'deposit-restriction',
        description,
        source: MARKERS_OF_HARM_AUTOMATION_SOURCE,
        ...buildDepositRestrictionConfig(restrictionPeriod),
    };
};

const buildShowPopupAction = (popupReference: string, enabled: boolean): ComplianceAutomationActionType => ({
    executionOrder: SHOW_POPUP_EXECUTION_ORDER,
    enabled,
    actionType: 'ShowPopup',
    config: {
        'popup-name': popupReference,
    },
});

const buildSetAccountStatusAction = (
    config: Record<string, unknown>,
    enabled: boolean
): ComplianceAutomationActionType => ({
    executionOrder: SET_ACCOUNT_STATUS_EXECUTION_ORDER,
    enabled,
    actionType: 'SetAccountStatus',
    config,
});

export const buildMarkersOfHarmAutomationDto = ({
    automationName,
    popupReference,
    popupTitle,
    active,
    appliedActionSettings,
}: {
    automationName: string;
    popupReference: string;
    popupTitle: string;
    active: boolean;
    appliedActionSettings: MarkersOfHarmAppliedActionSettingsType;
}): ComplianceAutomationType | null => {
    const actions: ComplianceAutomationActionType[] = [buildShowPopupAction(popupReference, active)];

    if (appliedActionSettings.appliedAction !== 'none') {
        const accountStatusConfig = buildSetAccountStatusConfig(
            appliedActionSettings.appliedAction,
            appliedActionSettings.restrictionPeriod ?? ''
        );

        if (accountStatusConfig === null) {
            return null;
        }

        actions.push(buildSetAccountStatusAction(accountStatusConfig, active));
    }

    return {
        name: automationName,
        description: `Markers of Harm Pop-Up automation for "${popupTitle.trim()}".`,
        enabled: active,
        actions,
    };
};

export const hydrateAppliedActionSettingsFromAutomation = (
    automation: ComplianceAutomationType
): MarkersOfHarmAppliedActionSettingsType => {
    const accountStatusAction = automation.actions.find((action) => action.actionType === 'SetAccountStatus');

    if (accountStatusAction === undefined) {
        return { appliedAction: 'none' };
    }

    const statusToSet = accountStatusAction.config['status-to-set'];

    if (statusToSet === 'compliance-restriction') {
        const suspensionDuration = accountStatusAction.config['suspension-duration'];
        const restrictionPeriod =
            typeof suspensionDuration === 'number'
                ? (POPUP_COMPLIANCE_RESTRICTION_PERIOD_OPTIONS.find(
                      (option) => parseComplianceRestrictionDays(option.value) === suspensionDuration
                  )?.value ?? '7-days')
                : '7-days';

        return {
            appliedAction: 'compliance-restriction',
            restrictionPeriod,
        };
    }

    if (statusToSet === 'deposit-restriction') {
        if (accountStatusAction.config.permament === true) {
            return {
                appliedAction: 'deposit-restriction',
                restrictionPeriod: 'permanent',
            };
        }

        const suspensionDuration = accountStatusAction.config['suspension-duration'];
        const restrictionPeriod =
            typeof suspensionDuration === 'number'
                ? (POPUP_DEPOSIT_RESTRICTION_PERIOD_OPTIONS.find((option) => {
                      if (option.value === 'permanent') {
                          return false;
                      }

                      const config = buildDepositRestrictionConfig(option.value);
                      return config['suspension-duration'] === suspensionDuration;
                  })?.value ?? '24-hours')
                : '24-hours';

        return {
            appliedAction: 'deposit-restriction',
            restrictionPeriod,
        };
    }

    if (statusToSet === 'suspended') {
        return { appliedAction: 'none' };
    }

    return { appliedAction: 'none' };
};

export const isAppliedActionSettingsComplete = (
    appliedAction: PopupAppliedActionFormValueType,
    restrictionPeriod: PopupAppliedActionRestrictionPeriodFormValueType
): appliedAction is PopupAppliedActionType => {
    if (appliedAction === '') {
        return false;
    }

    if (showsAppliedActionRestrictionPeriod(appliedAction) && restrictionPeriod === '') {
        return false;
    }

    return true;
};
